import { FunctionComponent, RefObject } from 'react'
import { Table } from 'react-bootstrap'

import CurrencyLabel from 'components/util/currencyLabel'
import StatusBadge from 'components/ui/statusBadge'
import { useVirtualRows } from 'components/ui/useVirtualRows'
import { Transfer, TransferStatus } from '../types'
import { getProcedureDescription, getStatusMeta } from '../transferStatus'
import transfersCss from '../styles.module.scss'

type Column = {
  header: string
  /** Width in rem. Fixed so columns don't shift while rows are virtualized. */
  width: number
  render: (transfer: Transfer) => React.ReactNode
  /** Full text shown on hover (for cells cut with an ellipsis). */
  title?: (transfer: Transfer) => string | null | undefined
  muted?: boolean
}

const VALUE_COLUMN_WIDTH = 10

const textColumn = (
  header: string,
  width: number,
  value: (transfer: Transfer) => string | number | null | undefined,
  muted = true
): Column => ({
  header,
  width,
  muted,
  render: (t) => value(t),
  title: (t) => {
    const text = value(t)
    return text == null ? null : String(text)
  },
})

const statusLabel = (transfer: Transfer) =>
  transfer.descricaoStatus ?? getStatusMeta(transfer.status)?.title ?? transfer.status

const STATUS_COLUMN: Column = {
  header: 'Status',
  width: 9,
  render: (transfer) => (
    <StatusBadge tone={getStatusMeta(transfer.status)?.tone ?? 'neutral'}>
      {statusLabel(transfer)}
    </StatusBadge>
  ),
  title: statusLabel,
}

const BASE_COLUMNS: Column[] = [
  textColumn('Atendimento', 6.5, (t) => t.numeroAtendimento, false),
  STATUS_COLUMN,
  textColumn('Convênio', 10.5, (t) => t.descricaoConvenio, false),
  textColumn('Procedimento', 13.5, getProcedureDescription),
  textColumn('Data do procedimento', 10, (t) => t.dataProcedimento),
  textColumn('Data da alta', 7, (t) => t.dataAlta),
]

const PROTOCOL_COLUMN = textColumn('Data do protocolo', 8.5, (t) => t.dataProtocolo)

/** Columns shown for each status; pending titles have two extra columns. */
const getColumns = (status: TransferStatus, showGlosaStatus: boolean): Column[] => {
  if (status === TransferStatus.PAGO) {
    return [
      ...BASE_COLUMNS,
      textColumn('Data do fechamento', 9, (t) => t.dataFechamentoRepasse),
      textColumn('Data do pagamento', 9, (t) => t.dataPagamento),
    ]
  }

  if (status === TransferStatus.PENDENTE) {
    return [
      ...BASE_COLUMNS,
      PROTOCOL_COLUMN,
      textColumn('Classe do título', 9, (t) => t.classeDoTitulo),
      ...(showGlosaStatus
        ? [textColumn('Status glosa título', 10, (t) => t.statusGlosaTituloReceber)]
        : []),
    ]
  }

  return [...BASE_COLUMNS, PROTOCOL_COLUMN]
}

type TransferReportTableProps = {
  transfers: Transfer[]
  status: TransferStatus
  /** Only shown for insurance (convênio) pending titles. */
  showGlosaStatus?: boolean
  /** Scrolling container; only the rows visible in it are rendered. */
  scrollRef: RefObject<HTMLDivElement>
}

/** Empty row that keeps the scroll height of the rows not rendered. */
const SpacerRow: FunctionComponent<{ height: number; colSpan: number }> = ({
  height,
  colSpan,
}) =>
  height > 0 ? (
    <tr aria-hidden="true">
      <td colSpan={colSpan} style={{ height, padding: 0, border: 0 }} />
    </tr>
  ) : null

const TransferReportTable: FunctionComponent<TransferReportTableProps> = ({
  transfers,
  status,
  showGlosaStatus = false,
  scrollRef,
}) => {
  const columns = getColumns(status, showGlosaStatus)
  const total = transfers.reduce((sum, t) => sum + t.valorRepasse, 0)
  const rows = useVirtualRows(scrollRef, transfers.length)
  const minWidth =
    columns.reduce((sum, column) => sum + column.width, VALUE_COLUMN_WIDTH) + 'rem'

  return (
    <Table hover className={transfersCss.fixedTable} style={{ minWidth }}>
      <colgroup>
        {columns.map((column) => (
          <col key={column.header} style={{ width: `${column.width}rem` }} />
        ))}
        <col style={{ width: `${VALUE_COLUMN_WIDTH}rem` }} />
      </colgroup>
      <thead>
        <tr>
          {columns.map((column) => (
            <th key={column.header}>{column.header}</th>
          ))}
          <th className="num">Valor</th>
        </tr>
      </thead>
      <tbody>
        <SpacerRow height={rows.paddingTop} colSpan={columns.length + 1} />
        {transfers.slice(rows.start, rows.end).map((transfer, offset) => (
          <tr
            key={rows.start + offset}
            ref={offset === 0 ? rows.measureRow : undefined}
          >
            {columns.map((column) => (
              <td
                key={column.header}
                className={column.muted ? 'muted' : undefined}
                title={column.title?.(transfer) ?? undefined}
              >
                {column.render(transfer)}
              </td>
            ))}
            <td className="num fw-medium">
              <CurrencyLabel>{transfer.valorRepasse}</CurrencyLabel>
            </td>
          </tr>
        ))}
        <SpacerRow height={rows.paddingBottom} colSpan={columns.length + 1} />
        <tr className="total-row">
          <td colSpan={columns.length}>Total</td>
          <td className="num">
            <CurrencyLabel>{total}</CurrencyLabel>
          </td>
        </tr>
      </tbody>
    </Table>
  )
}

export default TransferReportTable
